/**
 * The lead file's Feedback line (073, backend lib/feedback.ts): where the
 * post-move feedback request stands — Scheduled / Sent / Reminder sent /
 * Rated / Cancelled — with an admin-only "Send now" while it has never gone.
 * Shown only once the job has a request (it was moved to Closed with
 * Feedback switched on).
 */

import React, { useState } from 'react';
import { CRM } from '../lib/theme';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { userTz, formatDateTimeInTz } from '../lib/time';
import { Icons } from '../lib/icons';

const T = CRM;
const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

export function FeedbackLine({ leadId, refreshKey }: { leadId: string; refreshKey?: string }) {
  const api = useApiClient();
  const { user } = useAuth();
  const tz = userTz(user);
  const { data, refetch } = useApi<any>(async () => (await api.getLeadFeedback(leadId)).data, { dependencies: [leadId, refreshKey] });
  const [busy, setBusy] = useState(false);
  // What the last Send now did — shown until the next one (a failed email is never silent).
  const [notice, setNotice] = useState<{ text: string; color: string } | null>(null);
  const r = data?.request;
  if (!r) return null;

  const at = (iso: string | null) => (iso ? formatDateTimeInTz(iso, tz) : '');
  const channels = [r.emailSentAt && 'email', r.textSentAt && 'text'].filter(Boolean).join(' and ');
  let status: React.ReactNode;
  let color = T.muted;
  switch (r.status) {
    case 'scheduled': status = `Scheduled for ${at(r.sendAt)}`; break;
    case 'sending': status = 'Sending…'; break;
    case 'sent':
      status = <>
        Sent {at(r.sentAt)}{channels ? ` (${channels})` : ''}
        {r.reminderSentAt ? ` · Reminder sent ${at(r.reminderSentAt)}`
          : r.reminderCancelledAt ? ' · Reminder cancelled'
          : r.reminderDueAt ? ` · Reminder due ${at(r.reminderDueAt)}` : ''}
        {r.reason ? ` · Not sent: ${r.reason}` : ''}
      </>;
      break;
    case 'rated':
      color = r.rating <= 3 ? T.red : T.green;
      status = <>Rated <span style={{ color: T.amber, letterSpacing: '1px' }}>{stars(r.rating)}</span> {at(r.ratedAt)}</>;
      break;
    case 'cancelled': status = `Cancelled${r.reason ? ` — ${r.reason}` : ''}`; break;
    case 'skipped': color = T.amber; status = `Not sent — ${r.reason ?? 'nothing could be sent'}`; break;
    case 'failed': color = T.red; status = `Failed — ${r.reason ?? 'see the feed'}`; break;
    default: status = r.status;
  }

  const sendNow = async () => {
    if (!window.confirm('Send the feedback request to this client now, skipping the wait?')) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = (await api.sendFeedbackNow(leadId)).data;
      const color = res?.outcome === 'sent' ? T.green : res?.outcome === 'partly' ? T.amber : T.red;
      setNotice({ text: res?.message || 'Done — see the feed.', color });
    } catch (e: any) {
      setNotice({ text: e?.message || 'Could not send it.', color: T.red });
    } finally { setBusy(false); refetch(); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap', background: T.panel, border: `1px solid ${T.border}`, borderRadius: '9px', padding: '9px 12px', marginBottom: '11px', fontSize: '12px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 700, color: T.text }}>
        <Icons.star size={14} aria-hidden="true" /> Feedback
      </span>
      <span style={{ color, minWidth: 0, overflowWrap: 'anywhere' }}>{status}</span>
      {r.status === 'rated' && r.comment && (
        <span style={{ flexBasis: '100%', color: T.dim, fontStyle: 'italic', overflowWrap: 'anywhere' }}>“{r.comment}”</span>
      )}
      {data.canSendNow && (
        <button onClick={sendNow} disabled={busy}
                style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', background: T.panel2, color: T.text, border: `1px solid ${T.border}` }}>
          {busy ? 'Sending…' : 'Send now'}
        </button>
      )}
      {notice && (
        <span role="status" style={{ flexBasis: '100%', color: notice.color, fontWeight: 600, overflowWrap: 'anywhere' }}>{notice.text}</span>
      )}
    </div>
  );
}
